import { useCallback, useEffect, useRef, useState } from "react";
import introJs from "intro.js";
import "intro.js/introjs.css";
import "./onboarding.css";
import { getTourSteps, ONBOARDING_VERSION } from "./tours";
import analytics from "../services/analytics";

const STORAGE_PREFIX = "digitomy:onboarding";
const AUTO_START_DELAY_MS = 450;

const storageKey = (userId, tourId) =>
  `${STORAGE_PREFIX}:${userId}:${tourId}:v${ONBOARDING_VERSION}`;

const readTourState = (userId, tourId) => {
  try {
    const value = window.localStorage.getItem(storageKey(userId, tourId));
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
};
const writeTourState = (userId, tourId, status) => {
  try {
    window.localStorage.setItem(
      storageKey(userId, tourId),
      JSON.stringify({
        status,
        version: ONBOARDING_VERSION,
        updatedAt: new Date().toISOString(),
      }),
    );
  } catch {
    // Onboarding still works when storage is unavailable; it simply will not persist.
  }
};

const resolveSteps = (tourId) =>
  getTourSteps(tourId).flatMap((step) => {
    if (!step.element) return [step];
    const element = document.querySelector(step.element);
    return element ? [{ ...step, element }] : [];
  });

export const useProductTour = ({ tourId, userId, ready = true }) => {
  const tourRef = useRef(null);
  const [isTourRunning, setIsTourRunning] = useState(false);

  const stopTour = useCallback(() => {
    const tour = tourRef.current;
    tourRef.current = null;
    setIsTourRunning(false);
    if (tour?.hasStarted()) {
      void tour.exit(true);
    }
  }, []);

  const startTour = useCallback(
    ({ force = false } = {}) => {
      if (!ready || !userId || tourRef.current) return false;

      const previousState = readTourState(userId, tourId);
      if (!force && previousState?.status) return false;

      const steps = resolveSteps(tourId);
      if (steps.length === 0) return false;

      let outcomeRecorded = false;
      const eventProperties = {
        tour_id: tourId,
        tour_version: ONBOARDING_VERSION,
        trigger: force ? "manual" : "automatic",
      };
      const recordOutcome = (status) => {
        if (outcomeRecorded) return;
        outcomeRecorded = true;
        writeTourState(userId, tourId, status);
        analytics.capture(`tour_${status}`, eventProperties);
      };

      const tour = introJs.tour().setOptions({
        steps,
        showProgress: true,
        showBullets: false,
        showStepNumbers: false,
        scrollToElement: true,
        scrollPadding: 32,
        disableInteraction: true,
        exitOnEsc: true,
        exitOnOverlayClick: false,
        nextLabel: "Next",
        prevLabel: "Back",
        doneLabel: "Finish",
        tooltipClass: "digitomy-tour-tooltip",
        highlightClass: "digitomy-tour-highlight",
        helperElementPadding: 8,
        overlayOpacity: 0.68,
      });

      tour
        .onStart(() => {
          setIsTourRunning(true);
          analytics.capture("tour_started", eventProperties);
        })
        .onComplete(() => recordOutcome("completed"))
        .onSkip(() => recordOutcome("skipped"))
        .onExit(() => {
          recordOutcome("dismissed");
          tourRef.current = null;
          setIsTourRunning(false);
        });

      tourRef.current = tour;
      void tour.start().catch(() => {
        tourRef.current = null;
        setIsTourRunning(false);
      });
      return true;
    },
    [ready, tourId, userId],
  );

  useEffect(() => {
    if (!ready || !userId || readTourState(userId, tourId)?.status) return undefined;

    const timer = window.setTimeout(() => {
      startTour();
    }, AUTO_START_DELAY_MS);

    return () => window.clearTimeout(timer);
  }, [ready, startTour, tourId, userId]);

  useEffect(() => stopTour, [stopTour]);

  return {
    isTourRunning,
    startTour: () => startTour({ force: true }),
    stopTour,
  };
};
